import React, { useState } from 'react';
import { supabase } from '../integrations/client.ts';
import { auth } from '../context/auth.ts';
import ThemeToggle from './ThemeToggle.jsx';
import SettingsModal from './SettingsModal.jsx';
import UpgradeModal from './UpgradeModal.jsx';
import { HiCog } from 'react-icons/hi';

function AccountPanel({ accountPanelState, onToggleAccountPanel, currentUser, onSignOut, theme, onToggleTheme, onRefreshProfile }) {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    if (accountPanelState === 'hidden') return null;
    
    const handleSignOut = () => {
        // TODO: Replace with AWS Cognito sign out logic
        onSignOut();
    };

    const currentTier = String((currentUser?.subscriptionStatus || currentUser?.subscriptionType || currentUser?.subscription_type || 'free')).toLowerCase();
    const isPremium = currentTier === 'premium';

    const handleSettingsClick = () => {
        if (isPremium) {
            setShowSettingsModal(true);
        } else {
            setShowUpgradeModal(true);
        }
    };

    const handleSettingsSaved = async () => {
        if (onRefreshProfile) {
            await onRefreshProfile();
        }
    };

    const handleUpgrade = async () => {
        try {
            // Get valid session (refreshes if expired)
            const { session, error: sessionError } = await auth.getValidSession();
            if (sessionError || !session) throw new Error('Authentication session expired. Please sign in again.');

            const { data: premiumTier, error: tierError } = await supabase
                .from('subscription_tiers')
                .select('stripe_price_id')
                .eq('id', 'premium')
                .single();

            if (tierError || !premiumTier?.stripe_price_id) {
                throw new Error('Premium plan is not configured. Please contact support.');
            }

            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            const res = await fetch(`${supabaseUrl}/functions/v1/create-checkout-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                },
                body: JSON.stringify({
                    priceId: premiumTier.stripe_price_id,
                    successUrl: `${window.location.origin}/?upgrade=success`,
                    cancelUrl: `${window.location.origin}/?upgrade=cancel`
                })
            });

            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Failed to create checkout session');
            if (json.url) {
                window.location.href = json.url;
            } else {
                throw new Error('Checkout URL not returned');
            }
        } catch (err) {
            alert(err.message || 'Unable to start upgrade checkout');
        }
    };

    const handleCancelSubscription = async () => {
        try {
            // Get valid session (refreshes if expired)
            const { session, error: sessionError } = await auth.getValidSession();
            if (sessionError || !session) throw new Error('Authentication session expired. Please sign in again.');

            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            const res = await fetch(`${supabaseUrl}/functions/v1/cancel-subscription`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                }
            });

            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Failed to cancel subscription');
            alert('Your subscription will be cancelled at the end of the current period.');
        } catch (err) {
            alert(err.message || 'Unable to cancel subscription');
        }
    };
    
    const handleManageBilling = async () => {
        try {
            // Get valid session (refreshes if expired)
            const { session, error: sessionError } = await auth.getValidSession();
            if (sessionError || !session) throw new Error('Authentication session expired. Please sign in again.');

            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            const res = await fetch(`${supabaseUrl}/functions/v1/create-portal-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                },
                body: JSON.stringify({
                    returnUrl: window.location.href
                })
            });

            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Failed to open billing portal');

            if (json.url) {
                window.location.href = json.url;
            }
        } catch (err) {
            alert(err.message || 'Unable to open billing portal');
        }
    };

    const handleDeleteAccount = async () => {
        if (deleteConfirmText !== 'DELETE') {
            alert('Please type DELETE to confirm account deletion');
            return;
        }

        setIsDeletingAccount(true);

        try {
            // Get valid session (refreshes if expired)
            const { session, error: sessionError } = await auth.getValidSession();
            if (sessionError || !session) {
                throw new Error('Authentication session expired. Please sign in again.');
            }

            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            const res = await fetch(`${supabaseUrl}/functions/v1/delete-account`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                }
            });

            const json = await res.json();

            if (!res.ok) {
                throw new Error(json.error || 'Failed to delete account');
            }

            // Account deleted successfully
            alert('Your account has been successfully deleted. All your data has been removed.');

            // Sign out and redirect to home
            await supabase.auth.signOut();
            window.location.href = '/';
        } catch (err) {
            console.error('Error deleting account:', err);
            alert(err.message || 'Unable to delete account. Please try again or contact support.');
            setIsDeletingAccount(false);
        }
    };

    return (
        <div className={`account-panel ${accountPanelState}`}>
            <div className="account-panel-content">
                <div className="account-panel-header">
                    <h3>Account</h3>
                    <button className="close-btn" onClick={onToggleAccountPanel}>×</button>
                </div>
                
                {/* User Profile Section */}
                {currentUser && (
                    <div className="user-profile-section">
                        <div className="user-avatar-container" style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '16px'
                        }}>
                            <div className="user-avatar" style={{
                                width: '56px',
                                height: '56px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '24px',
                                fontWeight: '600',
                                marginRight: '16px',
                                boxShadow: '0 2px 8px rgba(74, 144, 226, 0.2)'
                            }}>
                                {(currentUser.name?.[0] || currentUser.username?.[0] || currentUser.email?.[0] || 'U').toUpperCase()}
                            </div>
                            <div className="user-details">
                                <div className="user-name" style={{
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    marginBottom: '4px',
                                    color: '#333',
                                    lineHeight: '1.2',
                                    textAlign: 'left'
                                }}>
                                    {currentUser.name || currentUser.username || currentUser.email || 'User'}
                                </div>
                                <div className="user-email" style={{ 
                                    fontSize: '14px', 
                                    color: '#666',
                                    lineHeight: '1.2'
                                }}>
                                    {currentUser.email}
                                </div>
                            </div>
                        </div>
                        
                        {(currentUser.subscriptionStatus || currentUser.subscriptionType || currentUser.subscription_type) && (
                            <div className="subscription-badge" style={{ 
                                display: 'inline-flex',
                                alignItems: 'center',
                                width: '100%',
                                fontSize: '12px', 
                                padding: '2px', 
                                borderRadius: '16px', // 56px (avatar width) + 16px (avatar margin-right)
                                backgroundColor: currentTier === 'premium' ? 'transparent' : currentTier === 'free' ? 'transparent' : '#f5f5f5',
                                color: currentTier === 'premium' ? 'transparent' : currentTier === 'free' ? '#333333' : '#666',
                                textTransform: 'capitalize',
                                fontWeight: '600',
                                border: `1px solid ${currentTier === 'premium' ? 'transparent' : currentTier === 'free' ? 'transparent' : '#e0e0e0'}`
                            }}>
                                {currentTier === 'premium' ? (
                                    <span className="premium-badge">Premium Plan</span>
                                ) : (
                                    `${currentTier} Plan`
                                )}
                            </div>
                        )}
                    </div>
                )}
                
                <div className="account-menu-sections">
                    {/* Account Settings Section */}
                    {/* <div className="menu-section" style={{ padding: '16px 0' }}>
                        <div className="section-title" style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#999',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '0 24px 12px 24px'
                        }}>
                            Account Settings
                        </div>
                        
                        <div className="menu-item" style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px 24px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                            fontSize: '14px',
                            color: '#333'
                        }}>
                            Profile Settings
                        </div>
                        
                        <div className="menu-item theme-toggle-item" style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 24px',
                            fontSize: '14px',
                            color: '#333'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                Theme
                            </div>
                            <ThemeToggle theme={theme} onToggleTheme={onToggleTheme} />
                        </div>
                    </div> */}
                    
                    {/* Billing Section */}
                    <div className="menu-section" style={{
                        padding: '16px 0',
                        borderTop: '1px solid #f0f0f0'
                    }}>
                        <div className="section-title" style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#999',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '0 24px 12px 24px',
                            textAlign: 'left'
                        }}>
                            Billing
                        </div>

                        <div className="menu-item" style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px 24px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                            fontSize: '14px',
                            color: '#333'
                        }}>
                            <div style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>Billing</span>
                                {currentUser && currentTier === 'free' ? (
                                    <button onClick={handleUpgrade} style={{
                                        padding: '8px 12px',
                                        borderRadius: '4px',
                                        border: 'none',
                                        backgroundColor: '#333333',
                                        color: '#f8f9fa',
                                        cursor: 'pointer'
                                    }}>
                                        Upgrade to Premium
                                    </button>
                                ) : (
                                    <button onClick={handleManageBilling} style={{
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        border: 'none',
                                        backgroundColor: '#333333',
                                        color: '#f8f9fa',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        transition: 'background-color 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#444444';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#333333';
                                    }}>
                                        Manage Billing
                                    </button>
                                )}
                            </div>
                        </div>

                        <div
                            className="menu-item"
                            onClick={handleSettingsClick}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '12px 24px',
                                cursor: isPremium ? 'pointer' : 'not-allowed',
                                transition: 'background-color 0.2s ease',
                                fontSize: '14px',
                                color: isPremium ? '#333' : '#999',
                                opacity: isPremium ? 1 : 0.6
                            }}
                            onMouseEnter={(e) => {
                                if (isPremium) {
                                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (isPremium) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }
                            }}
                        >
                            <HiCog size={18} />
                            Settings
                        </div>
                    </div>

                    {/* Support Section */}
                    <div className="menu-section" style={{
                        padding: '16px 0',
                        borderTop: '1px solid #f0f0f0'
                    }}>
                        <div className="section-title" style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#999',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '0 24px 12px 24px',
                            textAlign: 'left'
                        }}>
                            Support
                        </div>

                        <div
                            className="menu-item"
                            onClick={() => window.location.href = 'mailto:support@teachingorb.com'}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px 24px',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                                fontSize: '14px',
                                color: '#333'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            Contact Support
                        </div>

                        <div
                            className="menu-item"
                            onClick={() => window.open('https://docs.teachingorb.com', '_blank')}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px 24px',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                                fontSize: '14px',
                                color: '#333'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            Help
                        </div>
                    </div>
                    
                    {/* Sign Out Section */}
                    <div className="menu-section" style={{
                        padding: '16px 0',
                        borderTop: '1px solid #f0f0f0'
                    }}>
                        <div className="menu-item logout-item" onClick={handleSignOut} style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px 24px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                            fontSize: '14px',
                            color: '#e74c3c',
                            fontWeight: '500'
                        }}>
                            Sign Out
                        </div>
                    </div>

                    {/* Danger Zone Section */}
                    <div className="menu-section" style={{
                        padding: '16px 0',
                        borderTop: '1px solid #f0f0f0'
                    }}>
                        <div className="section-title" style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#e74c3c',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '0 24px 12px 24px',
                            textAlign: 'left'
                        }}>
                            Danger Zone
                        </div>
                        <div className="menu-item" onClick={() => setShowDeleteModal(true)} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 24px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                            fontSize: '14px',
                            color: '#e74c3c',
                            fontWeight: '500'
                        }}>
                            <span>Delete Account</span>
                            <span style={{ fontSize: '12px', color: '#999' }}>Permanent</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Account Confirmation Modal */}
            {showDeleteModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '24px',
                        maxWidth: '500px',
                        width: '90%',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                    }}>
                        <h2 style={{
                            fontSize: '24px',
                            fontWeight: '600',
                            color: '#e74c3c',
                            marginBottom: '16px'
                        }}>
                            Delete Account
                        </h2>

                        <div style={{
                            fontSize: '14px',
                            color: '#333',
                            marginBottom: '16px',
                            lineHeight: '1.6'
                        }}>
                            <p style={{ marginBottom: '12px' }}>
                                <strong>Warning:</strong> This action cannot be undone. This will permanently delete your account and remove all your data from our servers.
                            </p>
                            <ul style={{
                                listStyleType: 'disc',
                                paddingLeft: '24px',
                                marginBottom: '12px'
                            }}>
                                <li>All your courses and learning progress will be deleted</li>
                                <li>Your profile and personal information will be removed</li>
                                {currentTier === 'premium' && (
                                    <li style={{ color: '#e74c3c', fontWeight: '500' }}>
                                        Your premium subscription will be cancelled immediately
                                    </li>
                                )}
                            </ul>
                            <p style={{ marginTop: '16px', fontWeight: '500' }}>
                                Please type <span style={{
                                    backgroundColor: '#f5f5f5',
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    fontFamily: 'monospace'
                                }}>DELETE</span> to confirm:
                            </p>
                        </div>

                        <input
                            type="text"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            placeholder="Type DELETE to confirm"
                            disabled={isDeletingAccount}
                            style={{
                                width: '100%',
                                padding: '10px',
                                fontSize: '14px',
                                border: '2px solid #e0e0e0',
                                borderRadius: '4px',
                                marginBottom: '20px',
                                boxSizing: 'border-box'
                            }}
                        />

                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'flex-end'
                        }}>
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setDeleteConfirmText('');
                                }}
                                disabled={isDeletingAccount}
                                style={{
                                    padding: '10px 20px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    borderRadius: '6px',
                                    border: '1px solid #e0e0e0',
                                    backgroundColor: 'white',
                                    color: '#333',
                                    cursor: isDeletingAccount ? 'not-allowed' : 'pointer',
                                    opacity: isDeletingAccount ? 0.5 : 1
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteAccount}
                                disabled={isDeletingAccount || deleteConfirmText !== 'DELETE'}
                                style={{
                                    padding: '10px 20px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    borderRadius: '6px',
                                    border: 'none',
                                    backgroundColor: '#e74c3c',
                                    color: 'white',
                                    cursor: (isDeletingAccount || deleteConfirmText !== 'DELETE') ? 'not-allowed' : 'pointer',
                                    opacity: (isDeletingAccount || deleteConfirmText !== 'DELETE') ? 0.5 : 1
                                }}
                            >
                                {isDeletingAccount ? 'Deleting...' : 'Delete My Account'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Settings Modal */}
            {showSettingsModal && (
                <SettingsModal
                    currentUser={currentUser}
                    onClose={() => setShowSettingsModal(false)}
                    onSettingsSaved={handleSettingsSaved}
                />
            )}

            {/* Upgrade Modal for Free Users */}
            {showUpgradeModal && (
                <UpgradeModal
                    onClose={() => setShowUpgradeModal(false)}
                    onUpgrade={() => {
                        setShowUpgradeModal(false);
                        handleUpgrade();
                    }}
                />
            )}
        </div>
    );
}

export default AccountPanel; 